import React, { useState, useEffect, useRef } from "react";
import { Typography, Button } from "antd";
import {
  LeftOutlined,
  RightOutlined,
  AppstoreOutlined,
  BranchesOutlined,
  FileTextOutlined,
  FileDoneOutlined,
  SafetyCertificateOutlined,
  FileExcelOutlined,
  CheckCircleFilled,
  EyeOutlined,
  ShopOutlined,
  CreditCardOutlined,
} from "@ant-design/icons";

const { Title, Paragraph } = Typography;

const FEATURE_CARDS = [
  {
    id: "lead-assign",
    badge: "LEAD ALLOCATION & INGESTION",
    icon: <AppstoreOutlined />,
    title: "Lead Assign",
    description: "Bulk import leads from CSV/Excel and assign them directly to your sales team in less than 1 min.",
    gradient: "linear-gradient(180deg, #ffffff 0%, #eff6ff 100%)",
    themeColor: "#2459d3",
    visualType: "lead-assign",
  },
  {
    id: "merge-companies",
    badge: "CROSS-RECORD UNIFICATION",
    icon: <BranchesOutlined />,
    title: "Merge Companies",
    description: "Unify parent and subsidiary company profiles into a master group to sync quotations, proposals, and invoices across all accounts.",
    gradient: "linear-gradient(180deg, #ffffff 0%, #eff6ff 100%)",
    themeColor: "#2459d3",
    visualType: "merge-companies",
  },
  {
    id: "quotation",
    badge: "INSTANT CPQ & ESTIMATION",
    icon: <FileTextOutlined />,
    title: "Quotation",
    description: "Generate itemized multi-version price quotes (v1, v2) with automatic tax calculations, line discounts, and instant PDF dispatch.",
    gradient: "linear-gradient(180deg, #ffffff 0%, #eff6ff 100%)",
    themeColor: "#2459d3",
    visualType: "quotation",
  },
  {
    id: "proposal",
    badge: "INTERACTIVE DEAL ROOM",
    icon: <FileDoneOutlined />,
    title: "Proposal",
    description: "Dispatch interactive sales proposals, track live client view engagement, and capture digital contract sign-offs with audit trails.",
    gradient: "linear-gradient(180deg, #ffffff 0%, #eff6ff 100%)",
    themeColor: "#2459d3",
    visualType: "proposal",
  },
  {
    id: "invoice",
    badge: "GST BILLING & PAYMENT GATEWAY",
    icon: <SafetyCertificateOutlined />,
    title: "Invoice",
    description: "1-click GST-compliant invoices (CGST, SGST, IGST) with integrated Razorpay payment links and real-time webhook settlements.",
    gradient: "linear-gradient(180deg, #ffffff 0%, #eff6ff 100%)",
    themeColor: "#2459d3",
    visualType: "invoice",
  },
];

function BenefitsSection({ currentPage }) {
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const maxScrollTranslateRef = useRef(0);
  const activeCardIndexRef = useRef(0);
  const [activeCardIndex, setActiveCardIndex] = useState(0);

  // Recalculate exact horizontal travel distance so all 5 cards are 100% revealed
  useEffect(() => {
    const updateDimensions = () => {
      if (trackRef.current && trackRef.current.parentElement) {
        const viewportWidth = trackRef.current.parentElement.clientWidth;
        const cards = trackRef.current.children;
        if (cards && cards.length > 0) {
          const lastCard = cards[cards.length - 1];
          const totalTrackWidth = lastCard.offsetLeft + lastCard.offsetWidth;
          const maxTranslate = Math.max(0, totalTrackWidth - viewportWidth + 80);
          maxScrollTranslateRef.current = maxTranslate;
        } else {
          const trackWidth = trackRef.current.scrollWidth;
          const maxTranslate = Math.max(0, trackWidth - viewportWidth + 120);
          maxScrollTranslateRef.current = maxTranslate;
        }
      }
    };

    updateDimensions();
    const timeoutId = setTimeout(updateDimensions, 150);
    window.addEventListener("resize", updateDimensions);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", updateDimensions);
    };
  }, []);

  // Vertical Scroll -> Horizontal Translation (High-Performance 60/120fps RAF Scroll)
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          if (!sectionRef.current || !trackRef.current) {
            ticking = false;
            return;
          }

          const rect = sectionRef.current.getBoundingClientRect();
          const totalScrollDistance = sectionRef.current.offsetHeight - window.innerHeight;

          if (totalScrollDistance > 0) {
            const scrolled = -rect.top;
            const progress = Math.min(Math.max(scrolled / totalScrollDistance, 0), 1);
            const translateX = progress * maxScrollTranslateRef.current;
            trackRef.current.style.transform = `translate3d(-${translateX}px, 0, 0)`;

            const cardIdx = Math.min(FEATURE_CARDS.length - 1, Math.floor(progress * FEATURE_CARDS.length * 0.99));
            if (cardIdx !== activeCardIndexRef.current) {
              activeCardIndexRef.current = cardIdx;
              setActiveCardIndex(cardIdx);
            }
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Navigation button handlers
  const scrollToIndex = (index) => {
    if (!sectionRef.current) return;
    const clampedIndex = Math.max(0, Math.min(FEATURE_CARDS.length - 1, index));
    activeCardIndexRef.current = clampedIndex;
    setActiveCardIndex(clampedIndex);

    const rect = sectionRef.current.getBoundingClientRect();
    const currentScrollY = window.scrollY;
    const sectionTop = currentScrollY + rect.top;
    const totalScrollDistance = sectionRef.current.offsetHeight - window.innerHeight;

    if (totalScrollDistance > 0) {
      const targetRatio = clampedIndex / (FEATURE_CARDS.length - 1);
      const targetScrollY = sectionTop + targetRatio * totalScrollDistance;

      window.scrollTo({
        top: targetScrollY,
        behavior: "smooth",
      });
    }
  };

  const handlePrev = () => scrollToIndex(activeCardIndex - 1);
  const handleNext = () => scrollToIndex(activeCardIndex + 1);

  return (
    <section className={`explore-features-section ${currentPage === "pricing" ? "pricing-page-explore" : ""}`} id="features" ref={sectionRef}>
      <div className="explore-sticky-viewport">
        <div className="explore-container">
          {/* HEADER ROW */}
          <div className="explore-header-row">
            <div className="explore-header-left">
              <div className="explore-section-tag">HIGH-VELOCITY SALES ENGINE</div>
              <Title level={2} className="explore-main-title">
                Explore features you need <br />
                <span className="explore-title-accent">for high-velocity sales closing</span>
              </Title>
              <Paragraph className="explore-subline">DealVoice brings structure to everyday sales workflows, unifying lead allocation, company hierarchies, price quotes, proposals, and GST invoicing into one seamless flow.</Paragraph>
            </div>

            <div className="explore-nav-controls">
              <div className="explore-nav-buttons">
                <Button shape="circle" size="large" icon={<LeftOutlined />} onClick={handlePrev} disabled={activeCardIndex === 0} className="explore-nav-btn" aria-label="Previous feature" />
                <Button shape="circle" size="large" icon={<RightOutlined />} onClick={handleNext} disabled={activeCardIndex === FEATURE_CARDS.length - 1} className="explore-nav-btn" aria-label="Next feature" />
              </div>
            </div>
          </div>

          {/* HORIZONTAL CARDS TRACK */}
          <div className="explore-track-viewport">
            <div className="explore-cards-track" ref={trackRef}>
              {FEATURE_CARDS.map((card, index) => (
                <div
                  className={`explore-feature-card ${index === activeCardIndex ? "card-focused" : ""}`}
                  key={card.id}
                  style={{
                    background: card.gradient,
                  }}
                  onClick={() => scrollToIndex(index)}
                >
                  {/* Card Header */}
                  <div className="ef-card-header">
                    <h3 className="ef-card-title">{card.title}</h3>
                    <p className="ef-card-desc">{card.description}</p>
                  </div>

                  {/* Card Graphic Area */}
                  <div className="ef-card-graphic-container">
                    {/* Visual 1: Lead Assign (Ultra-Minimal UI Skeleton) */}
                    {card.visualType === "lead-assign" && (
                      <div className="ef-visual-box assign-visual">
                        <div className="ef-mac-topbar">
                          <div className="ef-mac-dots">
                            <span className="ef-mac-dot ef-mac-dot-red" />
                            <span className="ef-mac-dot ef-mac-dot-yellow" />
                            <span className="ef-mac-dot ef-mac-dot-green" />
                          </div>
                          <span className="ef-mac-title">dealvoice • lead-assignment.app</span>
                        </div>

                        <div className="sk-minimal-container">
                          {/* File header strip */}
                          <div className="sk-file-strip">
                            <FileExcelOutlined style={{ color: "#2459d3", fontSize: "13px" }} />
                            <span className="sk-file-name">Q3_Enterprise_Leads.xlsx</span>
                            <span className="sk-file-badge">450 leads</span>
                          </div>

                          {/* Minimal Skeleton Wireframe Rows */}
                          <div className="sk-wireframe-rows">
                            <div className="sk-wireframe-row">
                              <span className="sk-dot-check" />
                              <div className="sk-bars-wrap">
                                <span className="sk-bar sk-w-65" />
                                <span className="sk-bar sk-w-35" />
                              </div>
                              <span className="sk-assign-arrow">→</span>
                              <span className="sk-user-pill">Priya S.</span>
                            </div>

                            <div className="sk-wireframe-row">
                              <span className="sk-dot-check" />
                              <div className="sk-bars-wrap">
                                <span className="sk-bar sk-w-50" />
                                <span className="sk-bar sk-w-40" />
                              </div>
                              <span className="sk-assign-arrow">→</span>
                              <span className="sk-user-pill">Karthik V.</span>
                            </div>

                            <div className="sk-wireframe-row">
                              <span className="sk-dot-check" />
                              <div className="sk-bars-wrap">
                                <span className="sk-bar sk-w-75" />
                                <span className="sk-bar sk-w-30" />
                              </div>
                              <span className="sk-assign-arrow">→</span>
                              <span className="sk-user-pill">Priya S.</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Visual 2: Merge Companies (UI Skeleton Hierarchy) */}
                    {card.visualType === "merge-companies" && (
                      <div className="ef-visual-box merge-visual">
                        <div className="ef-mac-topbar">
                          <div className="ef-mac-dots">
                            <span className="ef-mac-dot ef-mac-dot-red" />
                            <span className="ef-mac-dot ef-mac-dot-yellow" />
                            <span className="ef-mac-dot ef-mac-dot-green" />
                          </div>
                          <span className="ef-mac-title">dealvoice • company-sync.app</span>
                        </div>

                        <div className="sk-minimal-container">
                          {/* Master Parent Strip */}
                          <div className="sk-file-strip">
                            <ShopOutlined style={{ color: "#2459d3", fontSize: "13px" }} />
                            <div className="sk-bars-wrap" style={{ marginLeft: 6 }}>
                              <span className="sk-bar sk-w-65" />
                            </div>
                            <span className="sk-file-badge">Unified Master</span>
                          </div>

                          {/* Synced tree connector */}
                          <div className="merge-sync-hub">
                            <div className="sync-hub-line" />
                            <div className="sync-hub-pill">
                              <span className="sync-hub-dot" />
                              <span className="sk-bar sk-w-50" style={{ width: "60px", height: "4px", margin: 0 }} />
                            </div>
                            <div className="sync-hub-line" />
                          </div>

                          {/* 3 Synced Sub-Branches Grid */}
                          <div className="sk-branch-grid">
                            <div className="sk-branch-card">
                              <FileTextOutlined style={{ color: "#2459d3", fontSize: "13px" }} />
                              <div className="sk-bars-wrap" style={{ width: "100%", alignItems: "center" }}>
                                <span className="sk-bar sk-w-75" />
                                <span className="sk-bar sk-w-40" />
                              </div>
                            </div>
                            <div className="sk-branch-card">
                              <FileDoneOutlined style={{ color: "#16a34a", fontSize: "13px" }} />
                              <div className="sk-bars-wrap" style={{ width: "100%", alignItems: "center" }}>
                                <span className="sk-bar sk-w-65" />
                                <span className="sk-bar sk-w-45" />
                              </div>
                            </div>
                            <div className="sk-branch-card">
                              <SafetyCertificateOutlined style={{ color: "#2459d3", fontSize: "13px" }} />
                              <div className="sk-bars-wrap" style={{ width: "100%", alignItems: "center" }}>
                                <span className="sk-bar sk-w-70" />
                                <span className="sk-bar sk-w-35" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Visual 3: Quotation (UI Skeleton CPQ Table) */}
                    {card.visualType === "quotation" && (
                      <div className="ef-visual-box quotation-visual">
                        <div className="ef-mac-topbar">
                          <div className="ef-mac-dots">
                            <span className="ef-mac-dot ef-mac-dot-red" />
                            <span className="ef-mac-dot ef-mac-dot-yellow" />
                            <span className="ef-mac-dot ef-mac-dot-green" />
                          </div>
                          <span className="ef-mac-title">dealvoice • quotation-cpq.app</span>
                        </div>

                        <div className="sk-minimal-container">
                          {/* Quote Header Strip */}
                          <div className="sk-file-strip">
                            <FileTextOutlined style={{ color: "#2459d3", fontSize: "13px" }} />
                            <div className="sk-bars-wrap" style={{ marginLeft: 6 }}>
                              <span className="sk-bar sk-w-50" />
                            </div>
                            <span className="sk-file-badge">v2 Latest</span>
                          </div>

                          {/* Line Items Wireframe */}
                          <div className="sk-wireframe-rows">
                            <div className="sk-wireframe-row">
                              <span className="sk-dot-check" />
                              <div className="sk-bars-wrap">
                                <span className="sk-bar sk-w-75" />
                                <span className="sk-bar sk-w-35" />
                              </div>
                              <span className="sk-bar sk-w-25" style={{ width: 45, height: 8 }} />
                            </div>

                            <div className="sk-wireframe-row">
                              <span className="sk-dot-check" />
                              <div className="sk-bars-wrap">
                                <span className="sk-bar sk-w-60" />
                                <span className="sk-bar sk-w-40" />
                              </div>
                              <span className="sk-bar sk-w-25" style={{ width: 35, height: 8 }} />
                            </div>

                            {/* Summary strip */}
                            <div className="sk-summary-strip">
                              <div className="sk-bars-wrap">
                                <span className="sk-bar sk-w-40" />
                              </div>
                              <span className="sk-user-pill" style={{ color: "#2459d3", fontWeight: 800 }}>Net Total</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Visual 4: Proposal (UI Skeleton Deal Room) */}
                    {card.visualType === "proposal" && (
                      <div className="ef-visual-box proposal-visual">
                        <div className="ef-mac-topbar">
                          <div className="ef-mac-dots">
                            <span className="ef-mac-dot ef-mac-dot-red" />
                            <span className="ef-mac-dot ef-mac-dot-yellow" />
                            <span className="ef-mac-dot ef-mac-dot-green" />
                          </div>
                          <span className="ef-mac-title">dealvoice • proposal-room.app</span>
                        </div>

                        <div className="sk-minimal-container">
                          {/* Header Strip */}
                          <div className="sk-file-strip">
                            <FileDoneOutlined style={{ color: "#16a34a", fontSize: "13px" }} />
                            <div className="sk-bars-wrap" style={{ marginLeft: 6 }}>
                              <span className="sk-bar sk-w-55" />
                            </div>
                            <span className="sk-file-badge" style={{ background: "#f0fdf4", color: "#16a34a", borderColor: "#bbf7d0" }}>Signed ✓</span>
                          </div>

                          {/* Milestone Progress Skeleton */}
                          <div className="sk-wireframe-rows">
                            <div className="sk-wireframe-row">
                              <div className="milestone-icon-wrap" style={{ width: 18, height: 18, minWidth: 18 }}>
                                <EyeOutlined style={{ fontSize: "9px", color: "#2459d3" }} />
                              </div>
                              <div className="sk-bars-wrap">
                                <span className="sk-bar sk-w-65" />
                                <span className="sk-bar sk-w-35" />
                              </div>
                              <span className="sk-user-pill">Viewed</span>
                            </div>

                            <div className="sk-wireframe-row">
                              <div className="milestone-icon-wrap" style={{ width: 18, height: 18, minWidth: 18 }}>
                                <CheckCircleFilled style={{ fontSize: "9px", color: "#16a34a" }} />
                              </div>
                              <div className="sk-bars-wrap">
                                <span className="sk-bar sk-w-70" />
                                <span className="sk-bar sk-w-40" />
                              </div>
                              <span className="sk-user-pill" style={{ color: "#16a34a" }}>Signoff</span>
                            </div>

                            {/* Action footer */}
                            <div className="sk-summary-strip">
                              <div className="sk-bars-wrap">
                                <span className="sk-bar sk-w-50" />
                              </div>
                              <span className="sk-assign-arrow">→</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Visual 5: Invoice (UI Skeleton Billing) */}
                    {card.visualType === "invoice" && (
                      <div className="ef-visual-box invoice-visual">
                        <div className="ef-mac-topbar">
                          <div className="ef-mac-dots">
                            <span className="ef-mac-dot ef-mac-dot-red" />
                            <span className="ef-mac-dot ef-mac-dot-yellow" />
                            <span className="ef-mac-dot ef-mac-dot-green" />
                          </div>
                          <span className="ef-mac-title">dealvoice • gst-billing.app</span>
                        </div>

                        <div className="sk-minimal-container">
                          {/* Invoice Strip */}
                          <div className="sk-file-strip">
                            <SafetyCertificateOutlined style={{ color: "#2459d3", fontSize: "13px" }} />
                            <div className="sk-bars-wrap" style={{ marginLeft: 6 }}>
                              <span className="sk-bar sk-w-50" />
                            </div>
                            <span className="sk-file-badge" style={{ background: "#f0fdf4", color: "#16a34a", borderColor: "#bbf7d0" }}>PAID ✓</span>
                          </div>

                          {/* Line breakdown wireframe */}
                          <div className="sk-wireframe-rows">
                            <div className="sk-wireframe-row">
                              <span className="sk-dot-check" />
                              <div className="sk-bars-wrap">
                                <span className="sk-bar sk-w-60" />
                                <span className="sk-bar sk-w-30" />
                              </div>
                              <span className="sk-bar sk-w-25" style={{ width: 40, height: 8 }} />
                            </div>

                            <div className="sk-wireframe-row">
                              <span className="sk-dot-check" />
                              <div className="sk-bars-wrap">
                                <span className="sk-bar sk-w-50" />
                                <span className="sk-bar sk-w-35" />
                              </div>
                              <span className="sk-bar sk-w-25" style={{ width: 30, height: 8 }} />
                            </div>

                            {/* Gateway verification footer */}
                            <div className="sk-summary-strip">
                              <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1 }}>
                                <CreditCardOutlined style={{ color: "#2459d3", fontSize: "11px" }} />
                                <span className="sk-bar sk-w-50" style={{ width: 60 }} />
                              </div>
                              <span className="sk-min-status" style={{ fontSize: "0.58rem" }}>Settled ✓</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default BenefitsSection;
